import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { LoaderWrapper } from "./index";
import {
  getAssessorsForAssessment,
  updateAssessorStatus,
  submitAssessorAcceptanceToNABL,
  getAssessorProfile,
} from "../services/api";
import { encryptText } from "../utils/encryption";

// Decrypt data from URL
const decryptData = (encryptedData) => {
  try {
    const decodedString = atob(encryptedData.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodedString);
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

const AssessmentDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [assessors, setAssessors] = useState([]);
  const [labInfo, setLabInfo] = useState({
    appId: '',
    orgId: '',
    labName: '',
    labType: '',
    labId: ''
  });
  const [error, setError] = useState("");
  const [selectedActions, setSelectedActions] = useState({});
  const [rejectRemarks, setRejectRemarks] = useState({});
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [profileDialog, setProfileDialog] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [labTypeId, setLabTypeId] = useState(null);
  const [applicationStage, setApplicationStage] = useState(null);

    // Get application stage from API
  const getApplicationStage = async (appId) => {
    try {
      if (!appId) return;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/laboratory/application-stage/${appId}`);
      const result = await response.json();
      
      if (result.success) {
        setApplicationStage(result.stage);
      } else {
        setApplicationStage('1'); // Default to 1
      }
    } catch (error) {
      console.error('Error fetching application stage:', error);
      setApplicationStage('1'); // Default to 1
    }
  };


  useEffect(() => {
    // Decrypt data from URL - handle both old 'data' format and new individual params
    const searchParams = new URLSearchParams(location.search);
    const encryptedData = searchParams.get('data');
    const mode = searchParams.get('mode');
    
    let decryptedData = null;

    if (encryptedData) {
      // Old format with data parameter
      decryptedData = decryptData(encryptedData);
    } else {
      // New format with individual parameters
      const encryptedAppId = searchParams.get('appId');
      const encryptedOrgId = searchParams.get('orgId');
      const pass = searchParams.get('p');

      if (!encryptedAppId || !encryptedOrgId || !pass) {
        toast.error("Invalid assessment details link - missing parameters");
        navigate('/main/dashboard');
        return;
      }

      // For lab update mode, we'll get the lab info from the dashboard API
      if (mode === 'labUpdate') {
        // We'll set placeholder values and fetch actual data from dashboard
        decryptedData = {
          appId: encryptedAppId,
          orgId: encryptedOrgId,
          labName: 'Loading...',
          labType: 'Loading...',
          labId: '',
          mode: 'labUpdate'
        };
      } else {
        toast.error("Invalid assessment mode");
        navigate('/main/dashboard');
        return;
      }
    }

    if (!decryptedData) {
      toast.error("Failed to decrypt assessment data");
      navigate('/main/dashboard');
      return;
    }

    setLabInfo(decryptedData);

    // Get lab type ID from lab type name
    const labTypeMapping = {
      'Testing Laboratory': 1,
      'Calibration Laboratory': 2,
      'Medical Laboratory': 3
    };
    setLabTypeId(labTypeMapping[decryptedData.labType] || null);

    // Fetch application stage
    getApplicationStage(decryptedData.appId);
    
    // Fetch assessors data
    fetchAssessorsData(decryptedData.appId);
  }, [location, navigate]);

  const fetchAssessorsData = async (appId) => {
    try {
      setLoading(true);
      const response = await getAssessorsForAssessment(appId);

      if (response.data.success) {
        setAssessors(response.data.data);

        // Initialize selected actions and remarks from existing data
        const actions = {};
        const remarks = {};

        response.data.data.forEach(assessor => {
          if (assessor.CabAccepted === 1) {
            actions[assessor.AssessorAllotedId] = 'accept';
          } else if (assessor.CabAccepted === 2) {
            actions[assessor.AssessorAllotedId] = 'reject';
            remarks[assessor.AssessorAllotedId] = assessor.CABDenyReason || '';
          }
        });

        setSelectedActions(actions);
        setRejectRemarks(remarks);

        // Check if all assessors have been accepted/rejected
        checkSubmitButtonVisibility(response.data.data);
        setError("");
      } else {
        setError(response.data.message || "Failed to fetch assessors data");
      }
    } catch (error) {
      console.error("Error fetching assessors:", error);
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const checkSubmitButtonVisibility = (assessorsList) => {
    // Check if all assessors have been accepted or rejected
    const allActioned = assessorsList.every(
      assessor => assessor.CabAccepted === 1 || assessor.CabAccepted === 2
    );
    
    // Check if at least one is accepted
    const hasAccepted = assessorsList.some(
      assessor => assessor.CabAccepted === 1
    );
    
    console.log('🔍 checkSubmitButtonVisibility:', { applicationStage, allActioned, hasAccepted });
    setShowSubmitButton(applicationStage === '19e' && allActioned && hasAccepted);
  };

  // Re-check button visibility when applicationStage changes
  useEffect(() => {
    if (assessors.length > 0 && applicationStage) {
      checkSubmitButtonVisibility(assessors);
    }
  }, [applicationStage, assessors]);

  const handleActionChange = (assessorAllotedId, action) => {
    setSelectedActions(prev => ({
      ...prev,
      [assessorAllotedId]: action
    }));

    // Clear reject remark if switching to accept
    if (action === 'accept') {
      setRejectRemarks(prev => {
        const updated = { ...prev };
        delete updated[assessorAllotedId];
        return updated;
      });
    }
  };

  const handleRejectRemarkChange = (assessorAllotedId, value) => {
    setRejectRemarks(prev => ({
      ...prev,
      [assessorAllotedId]: value
    }));
  };

  const handleSubmitAction = async (id, assessorAllotedId) => {
    const action = selectedActions[id];

    if (!action) {
      toast.error('Please select an action (Accept/Reject)');
      return;
    }

    if (action === 'reject' && !rejectRemarks[id]?.trim()) {
      toast.error('Please enter reject remark');
      return;
    }

    try {
      setLoading(true);

      const response = await updateAssessorStatus({
        id,
        action,
        assessorAllotedId,
        rejectRemark: rejectRemarks[id] || '',
        appId: labInfo.appId,
        assessmentType: 'Main'
      });

      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh assessors data
        fetchAssessorsData(labInfo.appId);
      } else {
        toast.error(response.data.message || 'Failed to update assessor status');
      }
    } catch (error) {
      console.error('Error updating assessor status:', error);
      toast.error('Failed to update assessor status');
    } finally {
      setLoading(false);
    }
  };

  const handleViewProfile = async (allotedToId) => {
    navigate(`/main/assessor-profile?AssessorId=${encryptText(allotedToId)}`);
  };

  const handleSendToNABL = () => {
    setPasswordDialog(true);
  };

  const handlePasswordSubmit = async () => {
    if (!password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    try {
      setLoading(true);

      const response = await submitAssessorAcceptanceToNABL({
        appId: labInfo.appId,
        orgId: labInfo.orgId,
        password: password,
        assessmentType: 'Main'
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setPasswordDialog(false);
        setPassword('');
        // Redirect to dashboard
        setTimeout(() => {
          navigate('/main/dashboard');
        }, 1500);
      } else {
        toast.error(response.data.message || 'Failed to send acceptance to NABL');
      }
    } catch (error) {
      console.error('Error submitting to NABL:', error);
      toast.error(error.response?.data?.message || 'Failed to send acceptance to NABL');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionStatus = (assessor) => {
    if (assessor.CabAccepted === 1) {
      return { label: 'Accepted', color: 'success' };
    } else if (assessor.CabAccepted === 2) {
      return { label: 'Rejected', color: 'error' };
    }
    return { label: 'Pending', color: 'warning' };
  };

  if (loading && assessors.length === 0) {
    return (
      <LoaderWrapper
        loading={true}
        type="fullscreen"
        message="Loading Assessment Details..."
      />
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 8, minHeight: "100vh", bgcolor: "white", py: 4 }}>
        <Container maxWidth="lg">
          <Alert
            severity="error"
            sx={{
              mb: 2,
              border: "1px solid #f44336",
              bgcolor: "#ffebee",
            }}
          >
            {error}
          </Alert>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              variant="contained"
              onClick={() => fetchAssessorsData(labInfo.appId)}
              sx={{
                mr: 2,
                bgcolor: "#333",
                "&:hover": { bgcolor: "#555" },
              }}
            >
              Retry
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/main/dashboard")}
              sx={{
                borderColor: "#666",
                color: "#666",
                "&:hover": { borderColor: "#333", color: "#333" },
              }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 8,
        minHeight: "100vh",
        bgcolor: "white",
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/main/dashboard')}
            sx={{
              borderColor: "#666",
              color: "#666",
              "&:hover": { borderColor: "#333", color: "#333" },
            }}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#333" }}>
            Assessment Assessors
          </Typography>
        </Box>

        {/* Lab Information Card */}
        <Card elevation={1} sx={{ mb: 4, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <BusinessIcon sx={{ color: "#666", mr: 2, fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#333" }}
              >
                Laboratory Information
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#e3f2fd",
                    borderRadius: 2,
                    border: "1px solid #90caf9",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#1565c0", mb: 0.5, fontWeight: 500 }}
                  >
                    Laboratory Name:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "#0d47a1" }}
                  >
                    {labInfo.labName || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={2}>
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  Lab Type:
                </Typography>
                <Chip
                  label={labInfo.labType}
                  size="medium"
                  sx={{
                    bgcolor: "#f5f5f5",
                    color: "#666",
                    border: "1px solid #e0e0e0",
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  Lab ID:
                </Typography>
                <Chip
                  label={labInfo.labId}
                  size="medium"
                  color="warning"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Assessors Table */}
        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid #e0e0e0",
          }}
        >
          <Box
            sx={{ p: 3, bgcolor: "#fafafa", borderBottom: "1px solid #e0e0e0" }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              Selected Assessors
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                    Sr. No.
                  </TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                    Discipline
                  </TableCell>
                  {labTypeId !== 3 && (
                    <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                      Group
                    </TableCell>
                  )}
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                    Assessor
                  </TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                    Capacity
                  </TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                    From Date
                  </TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                    To Date
                  </TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                    Alloted on Date
                  </TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                    Profile
                  </TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                    Action
                  </TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                    Submit
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assessors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={labTypeId === 3 ? 12 : 13} align="center">
                      <Typography variant="body2" sx={{ color: "#666", py: 3 }}>
                        No assessors found for this assessment
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  assessors.map((assessor, index) => {
                    const actionStatus = getActionStatus(assessor);
                    const isDisabled = assessor.checkd === 1;
                    const stage = localStorage.getItem('stage');
                    const canEdit = stage === '19e';

                    return (
                      <TableRow
                        key={assessor.AssessorAllotedId}
                        hover
                        sx={{ "&:hover": { bgcolor: "#fafafa" } }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{assessor.disciplineName || 'N/A'}</TableCell>
                        {labTypeId !== 3 && (
                          <TableCell>{assessor.GroupName || 'N/A'}</TableCell>
                        )}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ color: '#666', mr: 1, fontSize: 18 }} />
                            {assessor.Name || 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell>{assessor.Capacity || 'N/A'}</TableCell>
                        <TableCell>{formatDate(assessor.DateFrom)}</TableCell>
                        <TableCell>{formatDate(assessor.DateTo)}</TableCell>
                        <TableCell>{formatDate(assessor.InsertDate)}</TableCell>
                        <TableCell>
                          <Tooltip title="View Profile">
                            <IconButton
                              size="small"
                              onClick={() => handleViewProfile(assessor.AllotedToId)}
                              sx={{ color: '#1976d2' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {assessor.CabAccepted === 1 || assessor.CabAccepted === 2 ? (
                          <Chip label={actionStatus.label} color={actionStatus.color} variant="contained" />
                          ) : (
                            <RadioGroup
                              value={selectedActions[assessor.id] || ''}
                              onChange={(e) =>
                                handleActionChange(
                                  assessor.id,
                                  e.target.value
                                )
                              }
                            >
                              <FormControlLabel
                                value="accept"
                                control={<Radio size="small" />}
                                label="Accept"
                              />
                              <FormControlLabel
                                value="reject"
                                control={<Radio size="small" />}
                                label="Reject"
                              />
                            </RadioGroup>
                          )}
                          {selectedActions[assessor.id] === 'reject' && (
                             <TextField
                              multiline
                              rows={3}
                              placeholder="Reject Remark*"
                              value={rejectRemarks[assessor.id] || ''}
                              onChange={(e) =>
                                handleRejectRemarkChange(
                                  assessor.id,
                                  e.target.value
                                )
                              }
                              size="small"
                              fullWidth
                              sx={{ minWidth: 200 }}
                            />)}
                            {assessor.CabAccepted === 2 && (
                              <Typography
                                variant="body2"
                                sx={{ color: "#d32f2f", mt: 1 }}
                              >
                                Reason: {assessor.CABDenyReason || 'N/A'}
                              </Typography>
                            )}
                        </TableCell>
                        <TableCell>
                          {assessor.CabAccepted == 0 ? (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSubmitAction(assessor.id, assessor.AssessorAllotedId)}
                              disabled={loading}
                              sx={{
                                bgcolor: "#1976d2",
                                "&:hover": { bgcolor: "#1565c0" },
                              }}
                            >
                              Submit
                            </Button>
                          ) : (
                            ''
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Send to NABL Button */}
        {showSubmitButton && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SendIcon />}
              onClick={handleSendToNABL}
              sx={{
                bgcolor: "#2e7d32",
                "&:hover": { bgcolor: "#1b5e20" },
                px: 4,
                py: 1.5,
              }}
            >
              Send to NABL
            </Button>
          </Box>
        )}
      </Container>

      {/* Password Dialog */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)}>
        <DialogTitle>Enter Your Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePasswordSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handlePasswordSubmit} variant="contained" disabled={loading}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Dialog */}
      <Dialog
        open={profileDialog}
        onClose={() => setProfileDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Assessor Profile
        </DialogTitle>
        <DialogContent>
          {selectedProfile && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Name:</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedProfile.Name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Email:</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedProfile.Email || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Phone:</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedProfile.Phone || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Organisation:</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedProfile.CurrentEmployer || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Qualifications:</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedProfile.Qualifications || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Experience:</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedProfile.Experience || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssessmentDetailsPage;
